/* eslint-disable react-hooks/exhaustive-deps, array-callback-return */
import React, { useMemo, useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { isEmpty } from 'lodash';
import { useSearchParams } from 'react-router-dom';
import { ButtonGroup, Button, Tooltip } from '@panwds/react-ui';
import { CheckboxFilledIcon, InfoIcon } from '@panwds/icons';
import { FilterBar, TreeSelectFilter, useFilters, SelectFilter } from '@panwds/react-filters';
import commonStyles from '../../../scss/StatusPageCommon.module.scss';
import styles from './GlobalProductHeader.module.scss';
import { CONTINENT_LIST, normalizeCountry, parseRegionFilterSelections } from '../../../shared/utils/regions';
import { resolveRegionStatus } from '../map/mapStatusHelpers';
import { STATUS_TYPE_ALL_SELECTED } from '../map/MapStatusBar';
import CustomDateRangePopover from '../../../shared/components/CustomDateRangePopover';
import { calendarDayToDate, toCalendarDayISO } from '../../../shared/utils/calendarDay';

const FILTER_CONTEXT_ID = 'StatusPageFilterBar';

const ALL_STATUS_OPTIONS = [
    { children: 'All', value: 'all' },
    { children: 'Operational', value: 'available' },
    { children: 'Degraded', value: 'degraded' },
    { children: 'Disrupted', value: 'disruption' },
];

const ALL_TIME_RANGE_OPTIONS = [
    { children: 'Past 7 days', value: '7d' },
    { children: 'Past 30 days', value: '30d' },
    { children: 'Past 90 days', value: '90d' },
    { children: 'Past 1 year', value: '1y' },
    { children: 'Custom', value: 'custom' },
];

const GlobalProductHeader = props => {
    const { regions, isFetchingRegions, products, statusType, statusesByProductCity, viewMode, onViewModeChange, onCustomHistoricRangeChange } = props;
    const { filters: pageFilters, setFilters, initialized: isFilterBarInitialized } = useFilters(FILTER_CONTEXT_ID);
    const [searchParams, setSearchParams] = useSearchParams();

    const [customHistoricRange, setCustomHistoricRange] = useState(null);
    const [isCustomPopoverOpen, setIsCustomPopoverOpen] = useState(false);
    const filterBarAnchorRef = useRef(null);

    // Hoist both filter selections to the top so the mutual-constraint memos can reference them.
    const selectedProducts = pageFilters?.products || [];

    const selectedRegions = useMemo(() => pageFilters?.region || [], [pageFilters]);

    // --- Mutual filter constraint: first-set-has-priority ---
    const [filterPriority, setFilterPriority] = useState(null); // 'products' | 'region' | null
    const prevProductsNonDefault = useRef(false);
    const prevRegionsNonDefault = useRef(false);

    // Regions that contain at least one selected product (used when products have priority).
    const regionsForSelectedProducts = useMemo(() => {
        if (filterPriority !== 'products' || !selectedProducts.length) return null;
        const sel = new Set(selectedProducts);
        return (regions || []).filter(r => r.product_mapping?.some(p => sel.has(p)));
    }, [filterPriority, selectedProducts, regions]);

    // Products available in the selected regions (used when regions have priority).
    const productsForSelectedRegions = useMemo(() => {
        if (filterPriority !== 'region') return null;
        const { selectedContinents, selectedCountries, selectedCities } =
            parseRegionFilterSelections(selectedRegions);
        const matchingRegions = (regions || []).filter(r =>
            selectedContinents.includes(r.continent) ||
            selectedCountries.includes(normalizeCountry(r.country)) ||
            selectedCities.includes(r.city)
        );
        const available = new Set(matchingRegions.flatMap(r => r.product_mapping || []));
        return (products || []).filter(p => available.has(p.product_type));
    }, [filterPriority, selectedRegions, regions, products]);

    // The region hierarchy is built from the full region list unless products have priority,
    // in which case only regions that map to at least one selected product are shown.
    const sourceRegions = useMemo(() => regionsForSelectedProducts ?? regions, [regionsForSelectedProducts, regions]);
    // --------------------------------------------------------

    const countryRegionsByContinent = useMemo(() => {
        const tmpMap = {};
        const micro_region = [];

        sourceRegions?.forEach(r => {
            const rCountry = normalizeCountry(r.country);
            const itExists = micro_region.find(m => m.id === rCountry);
            if (!itExists) {
                const cities = sourceRegions.filter(reg => {
                    if (rCountry === normalizeCountry(reg.country) && r.continent === reg.continent) {
                        return {
                            id: reg.city,
                            label: `${reg.city} - ${reg.edge_location}`,
                            value: reg.city,
                            content: (
                                <div className="twds-flex twds-items-center">
                                    <CheckboxFilledIcon className="twds-mr-2" />
                                    {reg.city}
                                </div>
                            ),
                            children: []
                        };
                    }
                }
                );
                micro_region.push({
                    ...r,
                    id: rCountry,
                    value: rCountry,
                    label: rCountry,
                    content: <div className="twds-flex twds-items-center">
                        <CheckboxFilledIcon className="twds-mr-2" /> {rCountry}
                    </div>,
                    children: [...new Set(cities.map(real => {
                        return {
                            id: real.city,
                            label: `${real.city} - ${real.edge_location}`,
                            value: real.city,
                            content: (
                                <div className="twds-flex twds-items-center">
                                    <CheckboxFilledIcon className="twds-mr-2" />
                                    {real.city}
                                </div>
                            ),
                            children: []
                        }
                    }))]
                })
            }
        });

        if (sourceRegions?.length) {
            sourceRegions.forEach(region => {
                const continent = region.continent;
                const country = normalizeCountry(region.country);

                if (!continent || !country) {
                    return;
                }

                if (!tmpMap?.[continent]) {
                    tmpMap[continent] = {};
                }

                if (!tmpMap?.[continent]?.[country]) {
                    tmpMap[continent][country] = [];
                }

                tmpMap[continent][country].push(region);
            });
        }

        return tmpMap;
    }, [sourceRegions]);

    const statusesByCity = useMemo(() => {
        if (!statusesByProductCity || !regions?.length) return {};
        const cityStatusMap = {};
        regions.forEach(region => {
            const city = region.city;
            if (!city) return;
            const regionStatus = resolveRegionStatus(
                statusesByProductCity,
                region.product_mapping,
                city,
                selectedProducts,
            );
            if (!cityStatusMap[city] || regionStatus !== 'available') {
                cityStatusMap[city] = regionStatus;
            }
        });
        return cityStatusMap;
    }, [statusesByProductCity, regions, selectedProducts]);

    const selectedStatusType = pageFilters?.statusType || STATUS_TYPE_ALL_SELECTED;
    // Normalize to a Set for multi-value support (statusType may be comma-separated).
    // SIZE_3 = all statuses, treated the same as 'all'.
    const selectedStatusTypeSet = useMemo(() => {
        if (!selectedStatusType || selectedStatusType === 'all' || selectedStatusType === STATUS_TYPE_ALL_SELECTED) {
            return null; // null means no filter
        }
        return new Set(selectedStatusType.split(',').filter(Boolean));
    }, [selectedStatusType]);

    const regionsFilterItems = useMemo(() => {
        const tmpItems = [];

        CONTINENT_LIST.slice().forEach(continentName => {
            const continentFilterItem = {
                value: `continent-${continentName}`,
                label: continentName,
                type: 'continent',
                children: [],
            };
            const countryRegions = countryRegionsByContinent?.[continentName];

            if (!countryRegions) {
                return;
            }

            const sortedCountryNames = Object.keys(countryRegions).sort((a, b) => a.localeCompare(b));

            const usaNdx = sortedCountryNames.indexOf('USA');

            if (usaNdx > -1) {
                sortedCountryNames.splice(usaNdx, 1);
                sortedCountryNames.unshift('USA');
            }

            sortedCountryNames.forEach(countryName => {
                const countryFilterItem = {
                    value: `country-${countryName}`,
                    label: countryName,
                    type: 'country',
                    children: [],
                };

                const tmpRegions = countryRegions?.[countryName] || [];
                const citiesMap = {};

                tmpRegions.forEach(tmpRegion => {
                    const cityStatus = statusesByCity[tmpRegion.city] || 'available';
                    if (selectedStatusTypeSet && !selectedStatusTypeSet.has(cityStatus)) {
                        return;
                    }
                    citiesMap[tmpRegion.city] = 1;
                });

                const sortedCityNames = Object.keys(citiesMap).sort((a, b) => a.localeCompare(b));

                sortedCityNames.forEach(cityName => {
                    const cityFilterItem = {
                        value: `city-${cityName}`,
                        label: cityName,
                        type: 'city',
                    };

                    countryFilterItem.children.push(cityFilterItem);
                });

                if (countryFilterItem.children.length > 0) {
                    continentFilterItem.children.push(countryFilterItem);
                }
            });

            if (continentFilterItem.children.length > 0) {
                tmpItems.push(continentFilterItem);
            }
        });

        return tmpItems;
    }, [countryRegionsByContinent, statusesByCity, selectedStatusTypeSet]);

    const selectedRegionCities = useMemo(() => {
        if (isEmpty(selectedRegions)) return null;
        const parsed = parseRegionFilterSelections(selectedRegions);

        if (parsed.isAllSelected) return null;

        const cities = new Set(parsed.selectedCities);

        if (regions?.length && (parsed.selectedContinents.length || parsed.selectedCountries.length)) {
            regions.forEach(r => {
                if (!r.city) return;
                if (parsed.selectedContinents.includes(r.continent)) {
                    cities.add(r.city);
                } else if (parsed.selectedCountries.includes(normalizeCountry(r.country))) {
                    cities.add(r.city);
                }
            });
        }

        return cities.size > 0 ? [...cities] : null;
    }, [selectedRegions, regions]);

    const availableStatusOptions = useMemo(() => {
        if (!statusesByCity || !Object.keys(statusesByCity).length) return ALL_STATUS_OPTIONS;

        const presentStatuses = new Set(['available']);
        Object.entries(statusesByCity).forEach(([city, status]) => {
            if (selectedRegionCities && !selectedRegionCities.includes(city)) return;
            presentStatuses.add(status);
        });

        return ALL_STATUS_OPTIONS.filter(
            opt => opt.value === 'all' || presentStatuses.has(opt.value)
        );
    }, [statusesByCity, selectedRegionCities]);

    const allProductsFilterItems = useMemo(() => {
        const allProducts = [];
        const productSource = productsForSelectedRegions ?? products;

        if (productSource?.length) {
            productSource.filter(pr => pr.ui_tag !== 'internal_mapping')?.forEach(p => {
                allProducts.push({
                    children: p.product_type,
                    value: p.product_type,
                });
            });
        }

        allProducts.sort((a, b) => a.children.localeCompare(b.children));

        return allProducts;
    }, [productsForSelectedRegions, products]);

    // Reset all filters to their all-selected defaults. Must use setFilters here instead
    // of relying on FilterBar's built-in defaultValues reset, because defaultValues is set
    // from URL params (for view-switch persistence) — not the true "all selected" default.
    // These two memos use the raw API props directly, bypassing filterPriority constraints,
    // so reset always produces the genuine all-selected state regardless of which filter
    // had priority at the time the user clicked Reset.
    const fullProductsResetValues = useMemo(() =>
        (products || []).filter(pr => pr.ui_tag !== 'internal_mapping').map(p => p.product_type),
    [products]);

    const fullRegionsResetValues = useMemo(() => {
        if (!regions?.length) return [];
        const seen = new Set();
        const result = [];
        regions.forEach(r => {
            if (r.continent && !seen.has(r.continent)) {
                seen.add(r.continent);
                result.push(`continent-${r.continent}`);
            }
        });
        return result;
    }, [regions]);

    const handleReset = useCallback(() => {
        // Clear URL params eagerly so that if filterBarKey changes during this render
        // (because allProductsFilterItems.length changes as the constraint lifts),
        // the prevFilterBarKeyRef snapshot captures an already-cleared URL rather than
        // stale region/product selections.
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            next.delete('products');
            next.delete('regions');
            next.delete('timeRange');
            return next;
        }, { replace: true });
        setFilters({
            products: fullProductsResetValues,
            region: fullRegionsResetValues,
            statusType: STATUS_TYPE_ALL_SELECTED,
            timeRange: '90d',
        });
        setCustomHistoricRange(null);
        setIsCustomPopoverOpen(false);
    }, [setFilters, setSearchParams, fullProductsResetValues, fullRegionsResetValues]);

    // Open the custom date popover when the user selects 'custom' from the time range dropdown
    useEffect(() => {
        if (!isFilterBarInitialized) return;
        if (pageFilters?.timeRange === 'custom') {
            setIsCustomPopoverOpen(true);
        } else {
            setIsCustomPopoverOpen(false);
        }
    }, [pageFilters?.timeRange, isFilterBarInitialized]);

    // Track which filter (products or region) the user modified first so the
    // correct dropdown gets restricted.
    useEffect(() => {
        if (!isFilterBarInitialized) return;
        // Use the raw (unconstrained) full product list as the "all" baseline so that
        // the constraint narrowing allProductsFilterItems doesn't create false non-default
        // readings (e.g. 3 products selected vs a constrained list of 3 = prodNonDefault:false).
        const prodNonDefault = selectedProducts.length > 0 && !fullProductsResetValues.every(v => selectedProducts.includes(v));
        const regNonDefault = selectedRegions.length > 0 && !fullRegionsResetValues.every(v => selectedRegions.includes(v));

        if (prodNonDefault && !prevProductsNonDefault.current) {
            setFilterPriority(prev => prev || 'products');
        } else if (!prodNonDefault && prevProductsNonDefault.current) {
            setFilterPriority(prev => prev === 'products' ? (regNonDefault ? 'region' : null) : prev);
        }
        if (regNonDefault && !prevRegionsNonDefault.current) {
            setFilterPriority(prev => prev || 'region');
        } else if (!regNonDefault && prevRegionsNonDefault.current) {
            setFilterPriority(prev => prev === 'region' ? (prodNonDefault ? 'products' : null) : prev);
        }
        prevProductsNonDefault.current = prodNonDefault;
        prevRegionsNonDefault.current = regNonDefault;
    }, [selectedProducts, selectedRegions, isFilterBarInitialized, fullProductsResetValues, fullRegionsResetValues]);

    // Derived boolean: is the user's selection still "all regions" relative
    // to the unconstrained full list? Used to decide whether to auto-sync the store.
    const isRegionsAtFullDefault = useMemo(
        () => fullRegionsResetValues.length > 0 && fullRegionsResetValues.every(v => selectedRegions.includes(v)),
        [fullRegionsResetValues, selectedRegions]
    );

    // Constrained continent values derived from regionsForSelectedProducts (for symmetry).
    const constrainedContinentValues = useMemo(() => {
        if (!regionsForSelectedProducts) return null;
        const seen = new Set();
        const result = [];
        regionsForSelectedProducts.forEach(r => {
            if (r.continent && !seen.has(r.continent)) {
                seen.add(r.continent);
                result.push(`continent-${r.continent}`);
            }
        });
        return result;
    }, [regionsForSelectedProducts]);

    // Constrained product values derived from productsForSelectedRegions (parallel to
    // constrainedContinentValues). Used in the URL sync effect to detect when the product
    // store was narrowed programmatically by the region-priority constraint rather than by
    // an explicit user selection, so we don't write a phantom products param to the URL.
    const constrainedProductValues = useMemo(() => {
        if (!productsForSelectedRegions) return null;
        return productsForSelectedRegions
            .filter(p => p.ui_tag !== 'internal_mapping')
            .map(p => p.product_type);
    }, [productsForSelectedRegions]);

    // When region constraint activates (filterPriority === 'region'), sync pageFilters.products
    // down to only the constrained items so PANWDS sees "all items selected → All" on the
    // button label instead of a stale named list from the label cache.
    // When the constraint lifts, restore to the full unconstrained default.
    useEffect(() => {
        if (!isFilterBarInitialized) return;
        if (filterPriority === 'region' && productsForSelectedRegions) {
            const constrainedValues = productsForSelectedRegions
                .filter(p => p.ui_tag !== 'internal_mapping')
                .map(p => p.product_type);
            const storeMatchesConstraint = constrainedValues.length === selectedProducts.length &&
                constrainedValues.every(v => selectedProducts.includes(v));
            if (!storeMatchesConstraint) {
                setFilters(prev => ({ ...prev, products: constrainedValues }));
            }
        } else if (filterPriority !== 'region' && !productsForSelectedRegions) {
            // Constraint lifted — restore to the full unconstrained default if the store
            // currently holds a constrained subset (all values are valid unconstrained values,
            // but the count is smaller than the full list).
            const wasConstrained = selectedProducts.length > 0 &&
                selectedProducts.length < fullProductsResetValues.length &&
                selectedProducts.every(v => fullProductsResetValues.includes(v));
            if (wasConstrained) {
                setFilters(prev => ({ ...prev, products: fullProductsResetValues }));
            }
        }
    }, [filterPriority, productsForSelectedRegions, selectedProducts, fullProductsResetValues, isFilterBarInitialized, setFilters]);

    // When product constraint activates (filterPriority === 'products'), sync pageFilters.region
    // down to only the continent values present in the constrained region list so PANWDS sees
    // "all items selected → All" on the Regions button label.
    // When the constraint lifts, restore to the full unconstrained default.
    useEffect(() => {
        if (!isFilterBarInitialized) return;
        if (filterPriority === 'products' && constrainedContinentValues) {
            // Only sync when regions are still at the unconstrained default and the store
            // doesn't already hold exactly the constrained continent list.
            if (isRegionsAtFullDefault && constrainedContinentValues.length !== selectedRegions.length) {
                setFilters(prev => ({ ...prev, region: constrainedContinentValues }));
            }
        } else if (filterPriority !== 'products' && !constrainedContinentValues) {
            // Constraint lifted — restore to the full unconstrained default.
            const wasConstrained = selectedRegions.length > 0 &&
                selectedRegions.length < fullRegionsResetValues.length &&
                selectedRegions.every(v => fullRegionsResetValues.includes(v));
            if (wasConstrained) {
                setFilters(prev => ({ ...prev, region: fullRegionsResetValues }));
            }
        }
    }, [filterPriority, constrainedContinentValues, isRegionsAtFullDefault, selectedRegions, fullRegionsResetValues, isFilterBarInitialized, setFilters]);

    // When switching back to real-time mode, reset timeRange to the default (90d) so the
    // hidden Time Range pill doesn't carry a non-default value into the next historic session.
    // The existing sync-to-URL effect will automatically drop ?timeRange= from the URL
    // because it treats '90d' as the null/default case (desiredTimeRange = null).
    const prevViewModeRef = useRef(viewMode);
    useEffect(() => {
        if (prevViewModeRef.current === 'historic' && viewMode === 'realtime') {
            if (pageFilters?.timeRange && pageFilters.timeRange !== '90d') {
                setFilters(prev => ({ ...prev, timeRange: '90d' }));
            }
        }
        prevViewModeRef.current = viewMode;
    }, [viewMode]); // eslint-disable-line react-hooks/exhaustive-deps

    // Sync non-default product/region selections to URL params so selections survive
    // view mode switches (FilterBar remounts with these as defaultValues) and pages are shareable.
    // lastWrittenParamsRef deduplicates: only call setSearchParams when URL values actually
    // need to change, preventing unnecessary replaceState calls that trigger Chrome's
    // IPC flooding throttle (which delays all subsequent pushState/navigation calls by ~5s).
    const lastWrittenParamsRef = useRef({ products: null, regions: null, timeRange: null });
    useEffect(() => {
        if (!isFilterBarInitialized) return;

        // Use the raw (unconstrained) product/region lists as the "all" baseline so that
        // mutual-filter priority constraints don't cause false non-default writes to the URL.
        // Without this, selecting products narrows the visible region list, which then makes
        // the still-full selectedRegions look like a non-default subset → regions get written
        // to the URL with the full continent list → historic view reads them as an explicit
        // (narrowed) selection and shows a phantom regions filter.
        //
        // The additional constraint-value checks handle the case where the constraint effect
        // has already synced the store (e.g. selectedRegions narrowed to constrainedContinentValues).
        // In that scenario fullRegionsResetValues.every(…) is false, but the user still hasn't
        // explicitly picked a region — so we treat it as "effectively all" here too.
        const isAllProds =
            selectedProducts.length === 0 ||
            fullProductsResetValues.every(v => selectedProducts.includes(v)) ||
            // Region-priority constraint narrowed products to the constrained list — not a user selection.
            (filterPriority === 'region' && constrainedProductValues !== null &&
                constrainedProductValues.length === selectedProducts.length &&
                constrainedProductValues.every(v => selectedProducts.includes(v)));

        const regionIsEffectivelyAll =
            selectedRegions.length === 0 ||
            fullRegionsResetValues.every(v => selectedRegions.includes(v)) ||
            // Product-priority constraint narrowed regions to constrainedContinentValues — not a user selection.
            (filterPriority === 'products' && constrainedContinentValues !== null &&
                constrainedContinentValues.length === selectedRegions.length &&
                constrainedContinentValues.every(v => selectedRegions.includes(v)));

        const desiredProducts = isAllProds ? null : selectedProducts.join(',');
        const desiredRegions = regionIsEffectivelyAll ? null : selectedRegions.join(',');
        const selectedTimeRange = pageFilters?.timeRange;
        const desiredTimeRange = (selectedTimeRange && selectedTimeRange !== '90d') ? selectedTimeRange : null;

        if (lastWrittenParamsRef.current.products === desiredProducts &&
            lastWrittenParamsRef.current.regions === desiredRegions &&
            lastWrittenParamsRef.current.timeRange === desiredTimeRange) {
            return;
        }

        lastWrittenParamsRef.current = { products: desiredProducts, regions: desiredRegions, timeRange: desiredTimeRange };

        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            if (desiredProducts) {
                next.set('products', desiredProducts);
            } else {
                next.delete('products');
            }
            if (desiredRegions) {
                next.set('regions', desiredRegions);
            } else {
                next.delete('regions');
            }
            if (desiredTimeRange) {
                next.set('timeRange', desiredTimeRange);
            } else {
                next.delete('timeRange');
            }
            return next;
        }, { replace: true });
    }, [selectedProducts, selectedRegions, pageFilters?.timeRange, isFilterBarInitialized, fullProductsResetValues, fullRegionsResetValues, filterPriority, constrainedProductValues, constrainedContinentValues]);

    // Capture URL params once per FilterBar lifecycle (at remount time only).
    // Reading searchParams directly inside statusViewFiltersConfig would make that memo
    // depend on searchParams, which is written by the sync-to-URL effect above —
    // creating a replaceState feedback loop that triggers Chrome's IPC flooding throttle.
    const filterBarInitUrlParamsRef = useRef({
        products: searchParams.get('products'),
        regions: searchParams.get('regions'),
        timeRange: searchParams.get('timeRange'),
    });

    const statusViewFiltersConfig = useMemo(() => {
        const filterConfig = [];

        // Restore from URL params when present (survives FilterBar remount on view mode switch).
        // Read from ref (captured at FilterBar mount time) rather than live searchParams to
        // avoid creating a reactive loop with the sync-to-URL effect.
        const urlProductsRaw = filterBarInitUrlParamsRef.current.products;
        const productDefaultValues = urlProductsRaw
            ? urlProductsRaw.split(',').filter(v => allProductsFilterItems.some(p => p.value === v))
            : allProductsFilterItems.map(p => p.value);

        const urlRegionsRaw = filterBarInitUrlParamsRef.current.regions;
        const regionDefaultValues = urlRegionsRaw
            ? urlRegionsRaw.split(',')
            : regionsFilterItems.map(r => r.value);

        filterConfig.push({
            Component: SelectFilter,
            context: {
                itemToString: item => item?.children?.toLowerCase?.(),
                enableSearch: true,
                allowDraftSelections: true,
                enableMultiSelect: true,
                enableSelectAll: true,
                fitToItemWidth: true,
                items: allProductsFilterItems,
            },
            label: 'Products',
            name: 'products',
            defaultValues: productDefaultValues,
            required: true,
            fitToItemWidth: true,
            width: 500,
        });

        filterConfig.push({
            Component: TreeSelectFilter,
            context: {
                placeholder: 'All',
                itemToString: item => item?.children?.toLowerCase?.(),
                enableSearch: true,
                allowDraftSelections: true,
                enableMultiSelect: true,
                enableSelectAll: true,
                items: regionsFilterItems,
                portalProps: {
                    className: commonStyles.regionTreeFilter,
                },
            },
            label: 'Regions',
            name: 'region',
            defaultValues: regionDefaultValues,
            required: true,
            static: true,
        });

        const urlTimeRange = filterBarInitUrlParamsRef.current.timeRange;
        const timeRangeDefault = ALL_TIME_RANGE_OPTIONS.some(o => o.value === urlTimeRange)
            ? urlTimeRange
            : '90d';
        filterConfig.push({
            Component: SelectFilter,
            context: {
                enableMultiSelect: false,
                items: ALL_TIME_RANGE_OPTIONS,
            },
            label: 'Time Range',
            name: 'timeRange',
            defaultValues: timeRangeDefault,
            required: true,
            static: true,
        });

        filterConfig.push({
            Component: SelectFilter,
            context: {
                enableMultiSelect: false,
                items: availableStatusOptions,
            },
            label: 'Status',
            name: 'statusType',
            defaultValues: statusType || STATUS_TYPE_ALL_SELECTED,
            required: true,
            static: true,
        });

        return filterConfig;
    }, [allProductsFilterItems, regionsFilterItems, availableStatusOptions])

    // filterBarKey drives FilterBar remounts. It includes:
    // - rawProductsLen / regions.length: changes when API data first loads
    // - allProductsFilterItems.length: changes when the mutual-constraint narrows the visible
    //   product list (e.g. user selects a region). A remount is required here because
    //   PANWDS FilterBar does not update its button label when context.items changes
    //   without a remount — the dropdown list updates but the button text stays stale.
    //   This is safe: when the constraint is active, the URL sync effect treats constrained
    //   products as "effectively all" and does not write them to the URL, so the remounted
    //   FilterBar defaults to selecting all visible (constrained) items → button shows "All".
    const filterBarKey = useMemo(() => {
        const rawProductsLen = (products || []).filter(pr => pr.ui_tag !== 'internal_mapping').length;
        return `${FILTER_CONTEXT_ID}-${rawProductsLen}-${(regions || []).length}-${allProductsFilterItems.length}`;
    }, [products, regions, allProductsFilterItems.length]);

    // Update the URL param snapshot synchronously during render when filterBarKey changes,
    // using the "prevRef" pattern. This must happen before statusViewFiltersConfig reads
    // from filterBarInitUrlParamsRef so FilterBar remounts with the correct defaultValues
    // (preserving whatever products/regions the user had selected before the view switch).
    // A useEffect would fire after render and be too late — the FilterBar would already
    // have remounted with stale ref values.
    const prevFilterBarKeyRef = useRef(filterBarKey);
    if (prevFilterBarKeyRef.current !== filterBarKey) {
        prevFilterBarKeyRef.current = filterBarKey;
        filterBarInitUrlParamsRef.current = {
            products: searchParams.get('products'),
            regions: searchParams.get('regions'),
            timeRange: searchParams.get('timeRange'),
        };
    }

    // Show skeleton filter buttons while the FilterBar is remounting (key changed) and
    // hasn't emitted its first values yet. useLayoutEffect runs synchronously before paint
    // so the skeleton is visible on the very first frame after filterBarKey changes —
    // the blank gap never reaches the screen.
    const [isFilterBarMounting, setIsFilterBarMounting] = useState(false);
    useLayoutEffect(() => {
        setIsFilterBarMounting(true);
    }, [filterBarKey]); // eslint-disable-line react-hooks/exhaustive-deps
    useLayoutEffect(() => {
        // pageFilters populating is the signal that the remounted FilterBar has emitted
        // its initial values. We add a short delay before revealing the real FilterBar so
        // PANWDS can finish its internal settling (reset button visibility check, pill
        // layout measurement) while still hidden behind the skeleton — preventing visible
        // layout shifts.
        if (pageFilters && Object.keys(pageFilters).length > 0) {
            const id = setTimeout(() => setIsFilterBarMounting(false), 150);
            return () => clearTimeout(id);
        }
    }, [pageFilters]);

    /**
     * PRD AC2: scope indicator below the filter bar.
     * Format: "Products (1): Prisma Access · Regions (2): North America, Europe"
     * Only segments that have a non-default (filtered) selection are shown.
     */
    const scopeSummary = useMemo(() => {
        if (!isFilterBarInitialized) return null;

        const result = {};

        const selectedProductsForSummary = pageFilters?.products || [];
        // Only count products that exist in the current (possibly constrained) items list.
        // This prevents the summary from showing stale products during the render cycle
        // before the constraint sync effect updates the filter store.
        const validProductValues = new Set(allProductsFilterItems.map(p => p.value));
        const effectiveProducts = selectedProductsForSummary.filter(v => validProductValues.has(v));

        // The "auto-expected" product values given the current constraint state:
        // when region has priority, the constraint effect syncs products to the constrained
        // list. When there's no region constraint, the expected default is all products.
        const autoExpectedProducts = filterPriority === 'region' && productsForSelectedRegions
            ? productsForSelectedRegions.filter(p => p.ui_tag !== 'internal_mapping').map(p => p.product_type)
            : fullProductsResetValues;

        // Products segment: show only when the user has diverged from the auto-expected state.
        // This suppresses the segment when region is selected and products are still at their
        // constrained "all available" default — but shows it if the user also narrows products.
        const isProductsAtAutoExpected = autoExpectedProducts.length > 0 &&
            autoExpectedProducts.every(v => effectiveProducts.includes(v)) &&
            effectiveProducts.every(v => autoExpectedProducts.includes(v));
        if (!isProductsAtAutoExpected && effectiveProducts.length > 0) {
            result.productNames = effectiveProducts
                .map(v => allProductsFilterItems.find(p => p.value === v)?.children || v)
                .sort((a, b) => a.localeCompare(b));
        }

        // The "auto-expected" region values given the current constraint state.
        const autoExpectedRegions = filterPriority === 'products' && constrainedContinentValues
            ? constrainedContinentValues
            : fullRegionsResetValues;

        // Regions segment: show only when the user has diverged from the auto-expected state.
        if (!isEmpty(selectedRegions)) {
            const isRegionsAtAutoExpected = autoExpectedRegions.length > 0 &&
                autoExpectedRegions.every(v => selectedRegions.includes(v)) &&
                selectedRegions.every(v => autoExpectedRegions.includes(v));
            if (!isRegionsAtAutoExpected) {
                const { isAllSelected, allSelections } = parseRegionFilterSelections(selectedRegions);
                if (!isAllSelected && allSelections.length > 0) {
                    result.regionNames = [...allSelections].sort((a, b) => a.localeCompare(b));
                }
            }
        }

        return (result.productNames || result.regionNames) ? result : null;
    }, [pageFilters?.products, selectedRegions, allProductsFilterItems, isFilterBarInitialized, fullProductsResetValues, filterPriority, productsForSelectedRegions, constrainedContinentValues]);

    const [showAllProducts, setShowAllProducts] = useState(false);
    const [showAllRegions, setShowAllRegions] = useState(false);

    // Reset expand state when the selection changes so stale expanded state isn't shown
    const prevProductNamesRef = useRef(null);
    const prevRegionNamesRef = useRef(null);
    if (scopeSummary?.productNames !== prevProductNamesRef.current) {
        prevProductNamesRef.current = scopeSummary?.productNames;
        if (showAllProducts) setShowAllProducts(false);
    }
    if (scopeSummary?.regionNames !== prevRegionNamesRef.current) {
        prevRegionNamesRef.current = scopeSummary?.regionNames;
        if (showAllRegions) setShowAllRegions(false);
    }

    const MAX_VISIBLE = 3;

    // Stamp a data attribute on <html> so that downstream sticky elements can use
    // CSS to pick the correct hardcoded top offset for the two filter bar heights:
    // "base" (no scope summary) vs "with-summary" (scope summary row visible).
    useEffect(() => {
        document.documentElement.setAttribute(
            'data-filter-scope',
            scopeSummary ? 'active' : 'inactive'
        );
        return () => document.documentElement.removeAttribute('data-filter-scope');
    }, [scopeSummary]);

    // Measure the sticky filter bar's rendered height and write it to a CSS custom property
    // so that downstream sticky elements (e.g. MapStatusBar) can position themselves correctly
    // regardless of whether the scope summary line is visible.
    const filterBarStickyRef = useRef(null);
    useLayoutEffect(() => {
        const el = filterBarStickyRef.current;
        if (!el) return;
        const update = () => {
            // getBoundingClientRect().bottom gives the distance from the viewport top to the
            // bottom of the filter bar. When the element is in its sticky-pinned state this
            // is constant (= stickyTop + contentHeight) regardless of scroll position — which
            // is exactly the value downstream sticky elements need for their own `top`.
            // When the page is at scroll position 0 it equals the natural document offset.
            const bottom = el.getBoundingClientRect().bottom -20;
            document.documentElement.style.setProperty('--filter-bar-height', `${Math.round(bottom)}px`);
        };
        const observer = new ResizeObserver(update);
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    return (
        <>
        <div ref={filterBarStickyRef} className={`ssmutw-p-0 ssmutw-dark tw-dark ${commonStyles.filterBarSticky}`}>
            <div className={`ssmutw-text-left ${commonStyles.contentWidth}`}>
                <div className={styles.modeToggleRow}>
                    <ButtonGroup className={styles.segmentedGroup}>
                        <Button
                            appearance={viewMode === 'realtime' ? 'primary' : 'secondary'}
                            onClick={() => onViewModeChange('realtime')}
                        >
                            Real-Time
                        </Button>
                        <Button
                            appearance={viewMode === 'historic' ? 'primary' : 'secondary'}
                            onClick={() => onViewModeChange('historic')}
                        >
                            Historic
                        </Button>
                    </ButtonGroup>
                </div>
                <div className={commonStyles.filterBar}>
                    <div ref={filterBarAnchorRef} className={`xs:ssmutw-col-span-1 ssmutw-grid sm:ssmutw-col-span-1 md:ssmutw-col-span-2 lg:ssmutw-col-span-2 ssmutw-col-span-2 ssmutw-gap-2${viewMode === 'realtime' ? ` ${styles.hideTimeRange}` : ''}`}>
                        {isFilterBarMounting && (
                            <div className={styles.filterSkeletonRow} aria-hidden="true">
                                <div className={styles.filterSkeletonPill} style={{ width: 130 }} />
                                <div className={styles.filterSkeletonPill} style={{ width: 110 }} />
                                <div className={styles.filterSkeletonPill} style={{ width: 120 }} />
                            </div>
                        )}
                        <FilterBar
                            isLoading={isFetchingRegions}
                            name={FILTER_CONTEXT_ID}
                            key={filterBarKey}
                            filters={statusViewFiltersConfig}
                            hideAddFilter
                            saveOnUnmount={false}
                            onResetClicked={handleReset}
                            className={`ssmutw-p-0 ssmutw-w-full md:ssmutw-w-auto${isFilterBarMounting ? ' ssmutw-invisible ssmutw-h-0 ssmutw-overflow-hidden' : ''}`}
                        />

                        <div className={commonStyles.filterBarSelectionSummary}>
                            {scopeSummary && (
                                <span>
                                    {scopeSummary.productNames && (() => {
                                        const names = scopeSummary.productNames;
                                        const visible = showAllProducts ? names : names.slice(0, MAX_VISIBLE);
                                        const hidden = names.length - visible.length;
                                        return (
                                            <span>
                                                {`Products (${names.length}): ${visible.join(' · ')}`}
                                                {hidden > 0 && (
                                                    <button
                                                        className={styles.summaryMoreBtn}
                                                        onClick={() => setShowAllProducts(true)}
                                                    >
                                                        {`+${hidden} more`}
                                                    </button>
                                                )}
                                                {showAllProducts && names.length > MAX_VISIBLE && (
                                                    <button
                                                        className={styles.summaryMoreBtn}
                                                        onClick={() => setShowAllProducts(false)}
                                                    >
                                                        {' show less'}
                                                    </button>
                                                )}
                                            </span>
                                        );
                                    })()}
                                    {scopeSummary.productNames && scopeSummary.regionNames && (
                                        <span>{'  ·  '}</span>
                                    )}
                                    {scopeSummary.regionNames && (() => {
                                        const names = scopeSummary.regionNames;
                                        const visible = showAllRegions ? names : names.slice(0, MAX_VISIBLE);
                                        const hidden = names.length - visible.length;
                                        return (
                                            <span>
                                                {`Regions (${names.length}): ${visible.join(', ')}`}
                                                {hidden > 0 && (
                                                    <button
                                                        className={styles.summaryMoreBtn}
                                                        onClick={() => setShowAllRegions(true)}
                                                    >
                                                        {`+${hidden} more`}
                                                    </button>
                                                )}
                                                {showAllRegions && names.length > MAX_VISIBLE && (
                                                    <button
                                                        className={styles.summaryMoreBtn}
                                                        onClick={() => setShowAllRegions(false)}
                                                    >
                                                        {' show less'}
                                                    </button>
                                                )}
                                            </span>
                                        );
                                    })()}
                                </span>
                            )}
                        </div>
                        {filterPriority === 'products' && isFilterBarInitialized && (
                            <Tooltip label="Regions are limited to those available for your selected products" defaultPlacement="bottom">
                                <span className={styles.constraintHint}>
                                    <InfoIcon size="xs" /> Region list filtered by product selection
                                </span>
                            </Tooltip>
                        )}
                        {filterPriority === 'region' && isFilterBarInitialized && (
                            <Tooltip label="Products are limited to those available in your selected regions" defaultPlacement="bottom">
                                <span className={styles.constraintHint}>
                                    <InfoIcon size="xs" /> Product list filtered by region selection
                                </span>
                            </Tooltip>
                        )}
                    </div>
                </div>
            </div>
        </div>

        <CustomDateRangePopover
            isOpen={isCustomPopoverOpen && viewMode === 'historic'}
            onClose={() => setIsCustomPopoverOpen(false)}
            anchorRef={filterBarAnchorRef}
            value={customHistoricRange
                ? { start: calendarDayToDate(customHistoricRange.start), end: calendarDayToDate(customHistoricRange.end) }
                : null
            }
            onChange={(start, end) => {
                // The uptime bars are UTC days: store the days of the cells the user picked as-is.
                const newRange = {
                    start: toCalendarDayISO(start),
                    end: toCalendarDayISO(end),
                };
                setCustomHistoricRange(newRange);
                if (onCustomHistoricRangeChange) onCustomHistoricRangeChange(newRange);
                setIsCustomPopoverOpen(false);
            }}
        />
        </>
    );
};

export default React.memo(GlobalProductHeader);
